/**
 * Client-side clock helpers.
 *
 * The browser clock is never trusted for deadlines. Every response carries the
 * server's `serverNow`; we store the offset between that and the local clock
 * and express every countdown in server terms. Changing the system clock
 * shifts both sides equally, so it buys no extra time — and the server
 * re-validates every deadline anyway.
 */

let offsetMs = 0;
let anchorServerMs = null;
let anchorMonotonicMs = null;

// A server timestamp is captured before the response reaches the browser, so
// adding part of the round trip normally improves the estimate. On a highly
// asymmetric mobile connection, however, half the RTT can put the client
// seconds ahead of the server. Keep that compensation small and let the next
// poll refine it instead of risking an early question boundary.
const MAX_TRANSIT_COMPENSATION_MS = 250;

function monotonicNow() {
  return typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now();
}

/**
 * Records the difference between server time and this browser's clock.
 */
export function syncServerTime(serverNowIso, requestStartedAt = null, responseReceivedAt = Date.now()) {
  if (!serverNowIso) return;

  const serverMs = Date.parse(serverNowIso);

  if (Number.isNaN(serverMs)) return;

  const receivedAt = Number.isFinite(responseReceivedAt) ? responseReceivedAt : Date.now();
  const startedAt = Number.isFinite(requestStartedAt) && requestStartedAt <= receivedAt ? requestStartedAt : receivedAt;
  const roundTripMs = Math.max(0, receivedAt - startedAt);

  // `serverNow` is sampled while the request is being handled. Estimating that
  // sample at the request midpoint avoids treating the whole network delay as
  // clock skew. Express the estimate at receipt time so it can be anchored to
  // a monotonic client clock below.
  const candidateAtReceipt = serverMs + Math.min(roundTripMs / 2, MAX_TRANSIT_COMPENSATION_MS);
  const currentEstimate = anchorServerMs === null ? null : serverNow();
  const nextEstimate = currentEstimate === null ? candidateAtReceipt : Math.max(candidateAtReceipt, currentEstimate);

  offsetMs = nextEstimate - receivedAt;
  anchorServerMs = nextEstimate;
  anchorMonotonicMs = monotonicNow();
}

/**
 * Current time in server terms, as a millisecond timestamp.
 */
export function serverNow() {
  if (anchorServerMs !== null && anchorMonotonicMs !== null) {
    return anchorServerMs + Math.max(0, monotonicNow() - anchorMonotonicMs);
  }

  return Date.now() + offsetMs;
}

/**
 * Milliseconds remaining until an ISO instant, floored at zero.
 */
export function msUntil(iso) {
  if (!iso) return 0;

  const target = Date.parse(iso);

  if (Number.isNaN(target)) return 0;

  return Math.max(0, target - serverNow());
}

/**
 * Whole seconds remaining, rounded up so a countdown shows "1" for the whole
 * final second rather than flicking to "0" early.
 */
export function secondsUntil(iso) {
  return Math.ceil(msUntil(iso) / 1000);
}

/**
 * "2 saat 5 dakika" style countdown, trimmed to the two largest useful units.
 */
export function formatCountdown(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));

  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;

  const pad = (n) => String(n).padStart(2, '0');

  if (days > 0) return `${days}g ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  if (hours > 0) return `${hours}:${pad(minutes)}:${pad(seconds)}`;

  return `${pad(minutes)}:${pad(seconds)}`;
}

/**
 * Milliseconds as seconds with one decimal, for evaluation times.
 */
export function formatSeconds(ms) {
  if (ms === null || ms === undefined) return '—';

  return (ms / 1000).toLocaleString(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

/**
 * A contest instant rendered in the contest's own timezone, so participants
 * always read the time the contest actually runs at.
 */
export function formatInZone(iso, timezone) {
  if (!iso) return '';

  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: timezone || undefined,
    }).format(new Date(iso));
  } catch (e) {
    // An unknown zone must not blank out the whole screen.
    return new Date(iso).toLocaleString();
  }
}
